import { Controller } from 'stimulus';
import {
  decimal,
  markupPercentage,
  money,
  quantity as decimalQuantity,
} from '@/js/shared/billables/decimal';

const EXPAND_ALL_EVENT = 'billable-item:expand-all';
const COLLAPSE_ALL_EVENT = 'billable-item:collapse-all';

export default class extends Controller {
  static targets = [
    'msrp',
    'clientPrice',
    'unitPrice',
    'markup',
    'quantity',
    'msrpDiscount',
    'tax',
    'shipping',
    'total',
    'expandToggle',
    'toggleIcon',
  ];

  #deleted = false;

  connect() {
    document.addEventListener(EXPAND_ALL_EVENT, this.#onExpandAll);
    document.addEventListener(COLLAPSE_ALL_EVENT, this.#onCollapseAll);
    this.#updateClientPriceError();
  }

  disconnect() {
    document.removeEventListener(EXPAND_ALL_EVENT, this.#onExpandAll);
    document.removeEventListener(COLLAPSE_ALL_EVENT, this.#onCollapseAll);
  }

  #clientPriceHandler = (event) => {
    this.#calculateItemMarkup(event);
    this.#updateItemOffRetail(event);
    this.#updateClientPriceError(event);
    this.#calculateItemTotal(event);
    this.#broadcastChanged();
  };
  clientPriceTargetConnected(element) {
    ['input', 'change'].forEach((event) => {
      element.addEventListener(event, this.#clientPriceHandler);
    });
  }
  clientPriceTargetDisconnected(element) {
    ['input', 'change'].forEach((event) => {
      element.removeEventListener(event, this.#clientPriceHandler);
    });
  }

  #unitPriceHandler = (event) => {
    this.#calculateItemClientPrice(event);
    this.#updateItemOffRetail(event);
    this.#updateClientPriceError(event);
    this.#calculateItemTotal(event);
    this.#broadcastChanged();
  };
  unitPriceTargetConnected(element) {
    ['input', 'change'].forEach((event) => {
      element.addEventListener(event, this.#unitPriceHandler);
    });
  }
  unitPriceTargetDisconnected(element) {
    ['input', 'change'].forEach((event) => {
      element.removeEventListener(event, this.#unitPriceHandler);
    });
  }

  #markupHandler = (event) => {
    this.#calculateItemClientPrice(event);
    this.#updateItemOffRetail(event);
    this.#updateClientPriceError(event);
    this.#calculateItemTotal(event);
    this.#broadcastChanged();
  };
  markupTargetConnected(element) {
    ['input', 'change'].forEach((event) => {
      element.addEventListener(event, this.#markupHandler);
    });
  }
  markupTargetDisconnected(element) {
    ['input', 'change'].forEach((event) => {
      element.removeEventListener(event, this.#markupHandler);
    });
  }

  #quantityHandler = (event) => {
    this.#calculateItemTotal(event);
    this.#broadcastChanged();
  };
  quantityTargetConnected(element) {
    ['input', 'change'].forEach((event) => {
      element.addEventListener(event, this.#quantityHandler);
    });
  }
  quantityTargetDisconnected(element) {
    ['input', 'change'].forEach((event) => {
      element.removeEventListener(event, this.#quantityHandler);
    });
  }

  #msrpHandler = (event) => {
    this.#updateItemOffRetail(event);
    this.#updateClientPriceError(event);
  };
  msrpTargetConnected(element) {
    ['input', 'change'].forEach((event) => {
      element.addEventListener(event, this.#msrpHandler);
    });
  }
  msrpTargetDisconnected(element) {
    ['input', 'change'].forEach((event) => {
      element.removeEventListener(event, this.#msrpHandler);
    });
  }

  #taxHandler = () => {
    this.dispatch('tax-changed');
  };
  taxTargetConnected(element) {
    element.addEventListener('change', this.#taxHandler);
  }
  taxTargetDisconnected(element) {
    element.removeEventListener('change', this.#taxHandler);
  }

  #shippingHandler = (event) => {
    this.#broadcastChanged();
  };
  shippingTargetConnected(element) {
    ['input', 'change'].forEach((event) => {
      element.addEventListener(event, this.#shippingHandler);
    });
  }
  shippingTargetDisconnected(element) {
    ['input', 'change'].forEach((event) => {
      element.removeEventListener(event, this.#shippingHandler);
    });
  }

  #calculateItemMarkup(event) {
    if (this.hasMarkupTarget) {
      const unitPrice = money(this.unitPriceTarget.value);
      const clientPrice = money(this.clientPriceTarget.value);
      let markup = decimal('0');
      if (clientPrice.gte(unitPrice) && clientPrice.gt('0') && unitPrice.gt('0')) {
        markup = clientPrice.minus(unitPrice).div(unitPrice).times('100');
      }
      this.markupTarget.value = markup.round(2).toFixed(2);
    }
  }

  get unitPrice() {
    if (this.hasUnitPriceTarget) {
      return money(this.unitPriceTarget.value);
    }
    return decimal('0');
  }

  get markupPercentage() {
    if (this.hasMarkupTarget) {
      return markupPercentage(this.markupTarget.value);
    }
    return decimal('0');
  }

  get clientPrice() {
    if (this.hasClientPriceTarget) {
      return money(this.clientPriceTarget.value);
    }
    return this.unitPrice;
  }

  get quantity() {
    if (this.hasQuantityTarget) {
      return decimalQuantity(this.quantityTarget.value);
    }
    return decimal('1');
  }

  get calculationInput() {
    return {
      id: this.persistedId,
      taxable: this.taxable,
      total: this.total,
      shipping: this.hasShippingTarget ? this.shippingTarget.value : '0',
    };
  }

  get persistedId() {
    const id = parseInt(this.element.dataset.id, 10);
    return Number.isNaN(id) ? null : id;
  }

  get total() {
    return this.clientPrice.times(this.quantity).round(2).toFixed(2);
  }

  get cost() {
    return this.unitPrice.times(this.quantity).round(2).toFixed(2);
  }

  get unitPriceMissing() {
    return this.unitPrice.eq('0');
  }

  get taxable() {
    return this.hasTaxTarget && this.taxTarget.checked;
  }

  set taxable(value) {
    if (this.hasTaxTarget) {
      this.taxTarget.checked = value;
    }
  }

  get markedForDestruction() {
    return this.#deleted;
  }

  #calculateItemTotal(event) {
    if (this.hasTotalTarget) {
      this.totalTarget.value = this.total;
    }
  }

  #calculateItemClientPrice(event) {
    if (this.hasClientPriceTarget) {
      const unitPrice = money(this.unitPriceTarget.value);
      if (unitPrice.eq('0')) {
        return;
      }

      const markupMultiplier = decimal(this.markupTarget.value).round(2).div('100').plus('1');

      this.clientPriceTarget.value = unitPrice.times(markupMultiplier).round(2).toFixed(2);
    }
  }

  get msrp() {
    if (this.hasMsrpTarget) {
      return money(this.msrpTarget.value);
    }
    return decimal('0');
  }

  #updateItemOffRetail(event) {
    if (this.hasMsrpDiscountTarget) {
      let res = '';

      if (this.clientPrice.gt('0') && this.msrp.gt('0')) {
        const offRetail = decimal('1').minus(this.clientPrice.div(this.msrp)).times('100').round();

        if (offRetail.gt('0')) {
          res = `${offRetail}% off retail`;
        }
      }

      this.msrpDiscountTarget.textContent = res;
    }
  }

  #updateClientPriceError(event) {
    if (!this.hasUnitPriceTarget || !this.hasClientPriceTarget) {
      return;
    }

    const valid = this.#deleted || this.clientPrice.gte(this.unitPrice);

    if (!valid) {
      this.element.classList.add('price-error');
      this.clientPriceTarget.classList.add('field--error');
      if (this.hasMsrpDiscountTarget) {
        this.msrpDiscountTarget.style.display = 'none';
      }
    } else {
      this.element.classList.remove('price-error');
      this.clientPriceTarget.classList.remove('field--error');
      if (this.hasMsrpDiscountTarget) {
        this.msrpDiscountTarget.style.display = '';
      }
    }

    this.dispatch('client-price-validity-changed', { detail: { valid } });
  }

  #broadcastChanged() {
    this.dispatch('changed');
  }

  toggle(event) {
    event.preventDefault();
    this.#setDetailsExpanded(!this.element.classList.contains('expanded'));
  }

  #onExpandAll = () => {
    this.#setDetailsExpanded(true);
  };

  #onCollapseAll = () => {
    this.#setDetailsExpanded(false);
  };

  #setDetailsExpanded(expanded) {
    this.element.classList.toggle('expanded', expanded);
    if (this.hasExpandToggleTarget) {
      this.expandToggleTarget.setAttribute('aria-expanded', expanded);
    }
    this.toggleIconTarget.classList.toggle('fa-caret-down', expanded);
    this.toggleIconTarget.classList.toggle('fa-caret-right', !expanded);
    this.dispatch('details-toggled', { detail: { expanded } });
  }

  delete(event) {
    event.preventDefault();
    this.#deleted = true;

    const gridBody = this.element.closest('.main-grid__body');

    const groupCheckbox = this.element.querySelector('.checkbox--group-billable-item');
    if (groupCheckbox?.checked) {
      groupCheckbox.checked = false;
      groupCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.#cleanUpGroupState();
    this.#parkDestroyFieldsOnForm();
    this.element.remove();
    this.dispatch('deleted', { target: gridBody, detail: { item: this.element } });
  }

  #parkDestroyFieldsOnForm() {
    const form = this.element.closest('form');
    const destroyField = this.element.querySelector('.field--billable-item-destroy');
    const idField = this.element.querySelector(':scope > input[name$="[id]"]');

    if (!form || !destroyField) {
      return;
    }

    destroyField.value = 'true';

    const parkingSlot = document.createElement('div');
    parkingSlot.classList.add('deleted-billable-item');
    parkingSlot.hidden = true;
    parkingSlot.append(...[idField, destroyField].filter(Boolean));
    form.append(parkingSlot);
  }

  #cleanUpGroupState() {
    const parentElement = this.element.parentElement;

    if (parentElement?.classList.contains('subitems')) {
      const remainingSecondaries = [...parentElement.children].filter((el) => el !== this.element);
      if (remainingSecondaries.length === 0) {
        this.#editGroupController?.dissolveGroup();
      }
    } else if (this.element.querySelector(':scope > .subitems .billable-item')) {
      this.#editGroupController?.dissolveGroup();
    }
  }

  get #editGroupController() {
    return this.application.getControllerForElementAndIdentifier(
      this.element,
      'billable-edit-group'
    );
  }
}
